// The actual notification element is defined in as-notification-card.ts
import './as-notification-card';
import type { AsNotificationCard, NotificationAction } from './as-notification-card';
import { liveAppData } from '../../store/store.ts';
import { MAX_STORED_NOTIFICATIONS } from '../../data/constants.ts';

/*
  This component groups all notification cards into a column. 
  As they appear and disappear, the stack grows and shrinks.

  This controls the lifetime and animation of the notification cards, with a default notification lifetime of 3 seconds.
  Notifications can be made to persist by setting the persist parameter to true.
  Notifications use css transitions to fade in and out, and after a certain time the cards are removed from the DOM.
  
  Created notifications resolves with an id, if detail.resolve exists within the received event. 
  This id can be used to close the notification by issuing an as-close-notification event.
  If no resolve/ reject or persist boolean is provided, the notification will be removed after 3 seconds.

  The notification type can be used to set the style of the notification card. In future we can define more styles. Notification cards will have <type>-notification class applied.

  Designed to live as a child element within as-app.

  Attributes:
    - default-timeout: number (optional)
      : The default timeout in milliseconds for non-persistent notifications. Defaults to 3000ms.

  Methods:
    - closeAll
      : Clears all remaining notification cards.
    - closeNotification(id: string)
      : Closes a notification by id.
    - notify(message: string, type: 'success' | 'error' | 'warning' | 'info', persist: boolean = false) => id
      : Spawns a notification card with the given message.

  Listened events:
    - as-notify: Creates a new notification
    - as-clear-notifications: Closes all notifications
    - as-close-notification: Closes a specific notification by id
*/ 

interface Notification {
  id: string;
  defaultText: string;
  dictKey: string;
  type: 'success' | 'error' | 'warning' | 'info';
  persist: boolean;
  element: HTMLElement;
}

import styleText from './as-notifications.css?raw';
  
export class AsNotifications extends HTMLElement {
  private notifications: Map<string, Notification> = new Map();
  private defaultTimeout = 3000; // Default timeout in milliseconds
  private static readonly sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();
  
  static get observedAttributes() {
    return ['default-timeout'];
  }
  
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [
      ...this.shadowRoot!.adoptedStyleSheets,
      AsNotifications.sheet
    ];
    
    // Bind methods to preserve context
    this.handleNotifyEvent = this.handleNotifyEvent.bind(this);
    this.handleClearNotificationsEvent = this.handleClearNotificationsEvent.bind(this);
    this.handleCloseNotificationEvent = this.handleCloseNotificationEvent.bind(this);
  }
  
  connectedCallback() {
    // console.log('AsNotifications: connectedCallback');
    // Get the actual as-app element by going up through shadow roots
    const root = this.getRootNode() as ShadowRoot;
    const parentApp = root.host;
    // console.log('AsNotifications: parent app element:', parentApp);
    if (parentApp) {
      // console.log('AsNotifications: Adding event listeners to parent app');
      parentApp.addEventListener('as-notify', this.handleNotifyEvent);
      parentApp.addEventListener('as-clear-notifications', this.handleClearNotificationsEvent);
      parentApp.addEventListener('as-close-notification', this.handleCloseNotificationEvent);
    } else {
      console.warn('AsNotifications: No parent app element found');
    }
    
    // Set default timeout from attribute if present
    const timeoutAttr = this.getAttribute('default-timeout');
    if (timeoutAttr && !isNaN(parseInt(timeoutAttr, 10))) {
      this.defaultTimeout = parseInt(timeoutAttr, 10);
    }
  }
  
  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (name === 'default-timeout' && newValue && !isNaN(parseInt(newValue, 10))) {
      this.defaultTimeout = parseInt(newValue, 10);
    }
  }
  
  disconnectedCallback() {
    const root = this.getRootNode() as ShadowRoot;
    const parentApp = root.host;
    if (parentApp) {
      parentApp.removeEventListener('as-notify', this.handleNotifyEvent);
      parentApp.removeEventListener('as-clear-notifications', this.handleClearNotificationsEvent);
      parentApp.removeEventListener('as-close-notification', this.handleCloseNotificationEvent);
    }
  }
  
  // Event handlers
  private handleNotifyEvent(event: CustomEvent) {
    // console.log('AsNotifications: Received as-notify event:', event.detail);
    const { defaultText, dictKey, type, persist, resolve, timeout, actions } = event.detail;
    const id = this.notify(defaultText, dictKey, type, persist, timeout);

    if (actions?.length) {
      const card = this.notifications.get(id)?.element as AsNotificationCard | undefined;
      card?.setActions(actions as NotificationAction[]);
    }

    if (typeof resolve === 'function') {
      resolve(id);
    }

    // Cache in notification history
    const entry = {
      id,
      defaultText: defaultText ?? '',
      dictKey: dictKey ?? '',
      type: type ?? 'info',
      timestamp: Date.now()
    };
    const history = [...liveAppData.notificationHistory, entry];
    if (history.length > MAX_STORED_NOTIFICATIONS) {
      history.splice(0, history.length - MAX_STORED_NOTIFICATIONS);
    }
    liveAppData.notificationHistory = history;
  }
  
  private handleClearNotificationsEvent() {
    // console.log('AsNotifications: Received as-clear-notifications event');
    this.closeAll();
  }
  
  private handleCloseNotificationEvent(event: CustomEvent) {
    // console.log('AsNotifications: Received as-close-notification event:', event.detail);
    if (event.detail?.id) {
      this.closeNotification(event.detail.id);
    }
  }
  
  // Public methods
  closeAll = () => {
    this.notifications.forEach((notification) => {
      this.animateAndRemove(notification);
    });
  }
  
  closeNotification = (id: string) => {
    const notification = this.notifications.get(id);
    if (notification) {
      this.animateAndRemove(notification);
    }
  }
  
  notify = (defaultText: string, dictKey: string, type: 'success' | 'error' | 'warning' | 'info', persist = false, timeout?: number) => {
    const id = `notification-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    
    // Create notification card element
    const notificationCard = document.createElement('as-notification-card');
    notificationCard.className = 'notification-container';
    notificationCard.setAttribute('type', type);
    notificationCard.setAttribute('default-text', defaultText);
    notificationCard.setAttribute('dict-key', dictKey);
    notificationCard.setAttribute('id', id);
    
    // Add to DOM with fade-in animation
    notificationCard.style.opacity = '0';
    notificationCard.style.transform = 'translateX(20px)';
    notificationCard.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
    
    this.shadowRoot?.appendChild(notificationCard);
    
    // Force reflow to ensure transition works
    void notificationCard.offsetWidth;
    
    // Start fade-in animation
    notificationCard.style.opacity = '1';
    notificationCard.style.transform = 'translateX(0)';
    
    // Store notification data
    const notification: Notification = {
      id,
      defaultText,
      dictKey,
      type,
      persist,
      element: notificationCard
    };
    
    this.notifications.set(id, notification);
    
    // If not persistent, set timeout to remove
    if (!persist) {
      // Use provided timeout or default to component's defaultTimeout
      const notificationTimeout = timeout ?? this.defaultTimeout;
      setTimeout(() => {
        this.closeNotification(id);
      }, notificationTimeout);
    }
    
    return id;
  }
  
  // Helper method to animate and remove notification
  private animateAndRemove(notification: Notification) {
    const { element, id } = notification;
    
    // Start fade-out animation
    element.style.opacity = '0';
    element.style.transform = 'translateX(20px)';
    
    // Remove element after animation completes
    setTimeout(() => {
      if (element.parentNode) {
        element.parentNode.removeChild(element);
      }
      this.notifications.delete(id);
    }, 300); // Matches the transition duration
  }
}

// Check if the element is already defined before registering it
if (!customElements.get('as-notifications')) {
  customElements.define('as-notifications', AsNotifications);
} else {
  console.warn("as-notifications already defined");
}
